import SirenParse from 'siren-parser';

import '../../common/bn-icon';
import '../actions/work-order-notes';
import './work-order-device-status';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, html, css } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';

const NO_DEVICE = 0;
const CONNECTED = 2;

class WorkOrderActionbar extends LitEntity {
  constructor() {
    super();
    this.deviceState = NO_DEVICE;
  }

  static get properties() {
    return {
      deviceState: { type: Number, attribute: false },
      latestEvent: { type: Object, attribute: false },
      latestEventHref: { type: String, attribute: false },
      isOpen: { type: Boolean },
      panel: { type: String },
      showDeviceStatus: { type: Boolean, attribute: false },
      showNotepad: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      :host {
        display: block;
        padding-bottom: 8px;
        pointer-events: none;
        position: sticky;
        bottom: 0;
        height: 4em;
      }
      .backdrop {
        z-index: 10;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        height: 100%;
        transition: background-color .2s;
        background-color: none;
        pointer-events: none;
      }
      .backdrop[is-open] {
        background-color: rgba(0,0,0,.6);
        pointer-events: all;
      }
      .bar {
        z-index: 11;
        display: block;
        pointer-events: all;
        position: absolute;
        bottom: 10px;
        right: 0;
        left: 0;
      }
      .buttons {
        margin: 0;
        display: flex;
        justify-content: center;
        background-color: var(--white-base);
        border-radius: 6px;
      }
      .buttons button {
        margin: .5em;
        padding: .3em .5em;
        text-decoration: none;
        flex-grow: 1;
      }
      .box {
        background: var(--surface);
        margin: 0;
        padding: 1em;
        display: none;
      }
      .box[visible] {
        display: block;
      }
      bn-icon {
        margin-right: .5em;
      }
    `];
  }

  render() {
    if (!this.entity) {
      return html``;
    }
    return html`
      <div class="backdrop" @mousedown="${this.close}" ?is-open="${this.isOpen}"></div>
      <div class="bar" @mousedown="${(e) => e.stopPropagation()}">
        <div class="boxes">
          <div class="box notes" ?visible="${this.panel === 'notes'}">
            <work-order-notes .href="${this.href}"></work-order-notes>
          </div>
          <div class="box device right" ?visible="${this.panel === 'device'}">
            <work-order-device-status .href="${this.deviceId}" @event="${this._onEventDevice}"></work-order-device-status>
          </div>
        </div>
        <div class="buttons">
          <button type="button" class="button button--outlined" @click="${() => this.toggle('notes')}" ?pressed="${this.panel === 'notes'}">
            <bn-icon name="fa fa-edit"></bn-icon>Notes
          </button>
          <button
            type="button"
            class="button button--contained right"
            @click="${() => this.toggle('device')}"
            ?pressed="${this.panel === 'device'}"
          >
            ${this._renderConnectionIcon()}Status
          </button>
        </div>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this._keyup = this._keyup.bind(this);
    window.addEventListener('keyup', this._keyup);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('keyup', this._keyup);
    clearTimeout(this._updateTimeout);
  }

  close() {
    this.isOpen = false;
    this.panel = undefined;
    document.activeElement && document.activeElement.blur();
  }

  toggle(panel) {
    if (panel === this.panel) {
      this.close();
      return;
    }
    this.isOpen = true;
    this.panel = panel;
  }

  _renderConnectionIcon() {
    switch (this.deviceState) {
    case CONNECTED: return html`<bn-icon name="fas fa-router"></bn-icon>`;
    case NO_DEVICE: return html`<bn-icon name="fas fa-exclamation-circle" style="color: var(--red-20)"></bn-icon>`;
    }
  }

  async _entityChanged(entity) {
    super._entityChanged(entity);
    this._updateDeviceState();
  }

  _onEventDevice(e) {
    const { event, isFresh } = e.detail;
    this.deviceState = (event && isFresh) ? CONNECTED : NO_DEVICE;
  }

  async _updateDeviceState() {
    const deviceLink = this.entity && this.entity.getSubEntityByClasses(['device', 'active']);
    this.deviceId = deviceLink && deviceLink.href;
    if (this.deviceId) {
      const { entity } = await entityStore.fetchWait(this.deviceId);
      this.deviceEntity = SirenParse(entity);
      this.latestEventHref = this.deviceEntity.getLink('https://api.binsentry.com/rel/event-latest')?.href;
      this._updateEvents();
    } else {
      this.deviceState = NO_DEVICE;
      this.deviceEntity = undefined;
    }
  }

  async _updateEvents() {
    clearTimeout(this._updateTimeout);
    if (!this.deviceEntity || !this.latestEventHref) {
      return;
    }
    await entityStore.fetchWait(this.latestEventHref);
    this._updateTimeout = setTimeout(() => this._updateEvents(), 15000);
  }

  _toggleDeviceStatus() {
    this.showDeviceStatus = !this.showDeviceStatus;
    this.showNotepad = false;
  }

  _toggleNotepad() {
    this.showNotepad = !this.showNotepad;
    this.showDeviceStatus = false;
  }

  _keyup(e) {
    if (e.key === 'Escape') {
      this.close();
    }
  }
}

window.customElements.define('work-order-actionbar', WorkOrderActionbar);
